
import { Suspense, lazy, useEffect } from "react";
import { preloadCriticalRoutes } from "@/lib/routePreloader";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { DashboardLayout } from "@/components/DashboardLayout";
import { ThemeProvider } from "@/components/theme-provider";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import "@/lib/i18n"; // Initialize i18n

// Lazy load all pages for code splitting
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const UpdatePassword = lazy(() => import("./pages/UpdatePassword"));
const SelectMembership = lazy(() => import("./pages/SelectMembership"));
const Checkout = lazy(() => import("./pages/Checkout"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const ReferralRedirect = lazy(() => import("./pages/ReferralRedirect"));
const Integration = lazy(() => import("./pages/connector/Integration"));
const MaterialCorporativo = lazy(() => import("./pages/MaterialCorporativo"));
const Templates = lazy(() => import("./pages/connector/Templates"));
const EditTemplate = lazy(() => import("./pages/connector/EditTemplate"));
const Campaigns = lazy(() => import("./pages/connector/Campaigns"));
const Lists = lazy(() => import("./pages/connector/Lists"));
const Funnels = lazy(() => import("./pages/connector/Funnels"));
const WhatsApp = lazy(() => import("./pages/setter/WhatsApp"));
const Agents = lazy(() => import("./pages/setter/Agents"));
const CreateAgent = lazy(() => import("./pages/setter/CreateAgent"));
const EditAgent = lazy(() => import("./pages/setter/EditAgent"));
const Blacklist = lazy(() => import("./pages/setter/Blacklist"));
const BayleisConexion = lazy(() => import("./pages/setter/BayleisConexion"));
const GoogleCalendarPage = lazy(() => import("./pages/setter/GoogleCalendarPage"));
const AgentesIA = lazy(() => import("./pages/setter/AgentesIA"));
const BuscarLids = lazy(() => import("./pages/setter/BuscarLids"));
const BaileysTest = lazy(() => import("./pages/BaileysTest"));
const Tutorials = lazy(() => import("./pages/Tutorials"));
const Settings = lazy(() => import("./pages/Settings"));
const CrmChat = lazy(() => import("./pages/CrmChat"));
const CrmUnified = lazy(() => import("./pages/CrmUnified"));
const CrmCapitalRelacional = lazy(() => import("./pages/CrmCapitalRelacional"));
const AsignarAgentes = lazy(() => import("./pages/AsignarAgentes"));
const WhatsAppTemplates = lazy(() => import("./pages/WhatsAppTemplates"));
const Calendar = lazy(() => import("./pages/Calendar"));
const CreateEvent = lazy(() => import("./pages/CreateEvent"));
const ReunionesAgendadas = lazy(() => import("./pages/ReunionesAgendadas"));
const NotFound = lazy(() => import("./pages/NotFound"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const GoogleCalendarTest = lazy(() => import("./pages/GoogleCalendarTest"));
const GoogleDiagnostic = lazy(() => import("./pages/GoogleDiagnostic"));
const OnboardingIndex = lazy(() => import("./pages/onboarding/OnboardingIndex"));
const ContactLaunch = lazy(() => import("./pages/onboarding/ContactLaunch"));
const PlanLaunch = lazy(() => import("./pages/onboarding/PlanLaunch"));
const Validations = lazy(() => import("./pages/onboarding/Validations"));
const Generate = lazy(() => import("./pages/onboarding/Generate"));
const ImportContacts = lazy(() => import("./pages/path1/ImportContacts"));
const Classify = lazy(() => import("./pages/path1/Classify"));
const Messages = lazy(() => import("./pages/path1/Messages"));
const Agent = lazy(() => import("./pages/path1/Agent"));
const Launch = lazy(() => import("./pages/path1/Launch"));
const CRMPath1 = lazy(() => import("./pages/path1/CRM"));
const Learning = lazy(() => import("./pages/path1/Learning"));
const ProfileHub = lazy(() => import("./pages/path2/ProfileHub"));
const ProfileOptimizer = lazy(() => import("./pages/path2/ProfileOptimizer"));
const PhotoGenerator = lazy(() => import("./pages/path2/PhotoGenerator"));
const CopyHackingReels = lazy(() => import("./pages/path2/CopyHackingReels"));
const MessagesUnified = lazy(() => import("./pages/unified/MessagesUnified"));
const AgentsUnified = lazy(() => import("./pages/unified/AgentsUnified"));
const MyProfile = lazy(() => import("./pages/MyProfile"));
const Notifications = lazy(() => import("./pages/Notifications"));
const About = lazy(() => import("./pages/About"));
const SystemPromptsManager = lazy(() => import("./pages/admin/SystemPromptsManager"));
const KnowledgeBaseManager = lazy(() => import("./pages/admin/KnowledgeBaseManager"));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="space-y-4 w-full max-w-md px-4">
      <Skeleton className="h-8 w-3/4" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-32 w-full" />
    </div>
  </div>
);

const queryClient = new QueryClient();

// OPTIMIZED: Preload critical routes after initial render
const App = () => {
  useEffect(() => {
    // Preload critical routes during idle time
    preloadCriticalRoutes();
  }, []);

  return (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider defaultTheme="light" storageKey="aspronet-ui-theme">
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            {/* OPTIMIZED: Error Boundary wraps entire app to prevent crashes */}
            <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/update-password" element={<UpdatePassword />} />
                <Route path="/ref" element={<ReferralRedirect />} />
                <Route path="/select-membership" element={<SelectMembership />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/book/:username/:eventSlug" element={<BookingPage />} />

              {/* Protected route for Chat (without header) */}
              <Route path="/chat" element={
                <ProtectedRoute>
                  <DashboardLayout hideHeader={true}>
                    <CrmChat />
                  </DashboardLayout>
                </ProtectedRoute>
              } />

              {/* Protected route for Capital Relacional (sidebar collapsed + hover) */}
              <Route path="/crm/capital-relacional" element={
                <ProtectedRoute>
                  <DashboardLayout sidebarCollapsed={true}>
                    <CrmCapitalRelacional />
                  </DashboardLayout>
                </ProtectedRoute>
              } />

              {/* Protected route for Agentes IA */}
              <Route path="/setter/agentes-ia" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <AgentesIA />
                  </DashboardLayout>
                </ProtectedRoute>
              } />

              {/* Protected routes */}
              <Route path="/*" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/dashboard" element={<Dashboard />} />

                      {/* Onboarding */}
                      <Route path="/onboarding" element={<PlanLaunch />} />
                      <Route path="/onboarding/index" element={<OnboardingIndex />} />
                      <Route path="/onboarding/validations" element={<Validations />} />
                      <Route path="/onboarding/generate" element={<Generate />} />

                      {/* Unified Pages */}
                      <Route path="/messages" element={<MessagesUnified />} />
                      <Route path="/agents" element={<AgentsUnified />} />

                      {/* Camino 1 */}
                      <Route path="/path1/import" element={<ImportContacts />} />
                      <Route path="/path1/classify" element={<Classify />} />
                      <Route path="/path1/messages" element={<MessagesUnified />} />
                      <Route path="/path1/agent" element={<AgentsUnified />} />
                      <Route path="/path1/launch" element={<Launch />} />
                      <Route path="/path1/crm" element={<CRMPath1 />} />
                      <Route path="/path1/learning" element={<Learning />} />

                      {/* Camino 2 */}
                      <Route path="/path2/profile" element={<ProfileHub />} />
                      <Route path="/path2/profile-optimizer" element={<ProfileOptimizer />} />
                      <Route path="/path2/photo-generator" element={<PhotoGenerator />} />
                      <Route path="/path2/reels" element={<CopyHackingReels />} />
                      <Route path="/path2/messages" element={<MessagesUnified />} />
                      <Route path="/path2/comments" element={<MessagesUnified />} />

                      {/* Biblioteca */}
                      <Route path="/library/templates" element={<MessagesUnified />} />
                      <Route path="/library/agents" element={<AgentsUnified />} />
                      <Route path="/library/materials" element={<MaterialCorporativo />} />
                      <Route path="/library/tutorials" element={<Tutorials />} />

                      {/* Datos */}
                      <Route path="/data/dashboard" element={<Dashboard />} />
                      <Route path="/data/campaigns" element={<Campaigns />} />

                      {/* Configuración */}
                      <Route path="/settings/company" element={<Settings />} />
                      <Route path="/settings/team" element={<Settings />} />

                      <Route path="/connector/integration" element={<Integration />} />
                      <Route path="/connector/templates" element={<Templates />} />
                      <Route path="/connector/templates/edit/:id" element={<EditTemplate />} />
                      <Route path="/connector/campaigns" element={<Campaigns />} />
                      <Route path="/connector/lists" element={<Lists />} />
                      <Route path="/connector/funnels" element={<Funnels />} />
                      <Route path="/setter/whatsapp" element={<WhatsApp />} />
                      <Route path="/setter/whatsapp-templates" element={<WhatsAppTemplates />} />
                      <Route path="/setter/agents" element={<Agents />} />
                      <Route path="/setter/agents/create" element={<CreateAgent />} />
                      <Route path="/setter/agents/:agentId/edit" element={<EditAgent />} />
                      <Route path="/setter/blacklist" element={<Blacklist />} />
                      <Route path="/setter/google-calendar" element={<GoogleCalendarPage />} />
                      <Route path="/setter/buscar-lids" element={<BuscarLids />} />
                      {/* AgentesIA moved outside to use hideHeader */}
                      <Route path="/setter-bayleis-conexion" element={<BayleisConexion />} />
                      <Route path="/baileys-test" element={<BaileysTest />} />
                      {/* /chat moved outside to use hideHeader */}
                      <Route path="/crm/dashboard" element={<CrmUnified />} />
                      {/* Capital Relacional moved outside to use sidebarCollapsed */}
                      <Route path="/crm/asignar-agentes" element={<AsignarAgentes />} />
                      <Route path="/calendar" element={<Calendar />} />
                      <Route path="/calendar/new-event" element={<CreateEvent />} />
                      <Route path="/calendar/events/:eventId/edit" element={<CreateEvent />} />
                      <Route path="/calendar/meetings" element={<ReunionesAgendadas />} />
                      <Route path="/calendar/google-test" element={<GoogleCalendarTest />} />
                      <Route path="/google-diagnostic" element={<GoogleDiagnostic />} />
                      <Route path="/tutorials" element={<Tutorials />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/my-profile" element={<MyProfile />} />
                      <Route path="/notifications" element={<Notifications />} />
                      <Route path="/about" element={<About />} />

                      {/* Admin Tools */}
                      <Route path="/admin/prompts" element={<SystemPromptsManager />} />
                      <Route path="/admin/knowledge" element={<KnowledgeBaseManager />} />

                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              </Routes>
            </Suspense>
            </ErrorBoundary>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
  );
};

export default App;
